import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

interface Heading {
  id: string
  text: string
  level: number
}

interface TableOfContentsProps {
  content: string
}

export function TableOfContents({ content }: TableOfContentsProps) {
  const [headings, setHeadings] = useState<Array<Heading>>([])
  const [activeId, setActiveId] = useState<string>('')
  const [mounted, setMounted] = useState(false)

  // Extract headings immediately (runs on both server and client)
  useEffect(() => {
    const headingRegex = /^(#{1,3})\s+(.+)$/gm
    const matches: Array<Heading> = []
    let match

    while ((match = headingRegex.exec(content)) !== null) {
      const level = match[1].length
      const text = match[2].trim()
      const id = text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim()

      matches.push({ id, text, level })
    }

    setHeadings(matches)
    setMounted(true)
  }, [content])

  // Only run intersection observer after component is mounted (client-only)
  useEffect(() => {
    if (!mounted || headings.length === 0 || typeof window === 'undefined')
      return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id)
          }
        })
      },
      { rootMargin: '-80px 0px -80% 0px' },
    )

    const headingElements = headings
      .map((h) => document.getElementById(h.id))
      .filter(Boolean)
    headingElements.forEach((el) => el && observer.observe(el))

    return () => {
      headingElements.forEach((el) => el && observer.unobserve(el))
    }
  }, [headings, mounted])

  if (headings.length === 0) return null

  const handleClick = (id: string) => {
    if (typeof window === 'undefined') return
    const element = document.getElementById(id)
    if (element) {
      const offset = 80
      const top = element.getBoundingClientRect().top + window.scrollY - offset
      window.scrollTo({ top, behavior: 'smooth' })
    }
  }

  return (
    <nav
      aria-label="On this page"
      className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto"
    >
      <p className="mb-3 text-caption font-semibold uppercase tracking-[0.12em] text-[var(--color-text-tertiary)]">
        On this page
      </p>
      {/* Hairline rail; the active entry's own left border sits over it. */}
      <ul className="border-l border-[var(--color-border)]">
        {headings.map((heading) => {
          const active = activeId === heading.id
          return (
            <li key={heading.id} className="-ml-px">
              <button
                type="button"
                onClick={() => handleClick(heading.id)}
                aria-current={active ? 'location' : undefined}
                style={{ paddingLeft: `${0.75 + (heading.level - 1) * 0.75}rem` }}
                className={cn(
                  'block w-full border-l py-1.5 pr-2 text-left text-body-sm leading-snug transition-colors duration-300 ease-brisk focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-inset focus-visible:ring-primary/50',
                  active
                    ? 'border-primary font-medium text-primary'
                    : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                )}
              >
                {heading.text}
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
